/**
 * Push-to-Talk hook - manages microphone capture and audio broadcast
 * 
 * Fixes applied:
 * - ptt:stop deferred until all arrayBuffer() promises flush (prevents last chunk loss)
 * - Document-level mouseup/touchend listeners so release is always captured
 *   (prevents stuck-transmitting when mouseLeave fires before mouseUp)
 * - Auto-stop after 60s max transmission
 * - On socket disconnect, auto-stop PTT
 * - pressedRef/isTxRef is reset on any error
 * - Bumped bitrate to 64kbps for better voice quality
 */
import { useRef, useCallback, useState } from 'react';
import { getSocket, playPTTBeep } from './useSocket';
import useStore from '../store/useStore';

const MAX_PTT_MS = 60000; // 60 seconds hard limit

export default function usePTT() {
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const startTimeRef = useRef(null);
  const isTxRef = useRef(false);
  const activeChannelRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const cleanupRef = useRef(null); // stores listener-cleanup function
  const releaseTimerRef = useRef(null); // 300ms release delay timer
  const stopTransmittingRef = useRef(null); // ref to avoid circular dep
  const pendingBuffersRef = useRef(0);     // count of unresolved arrayBuffer() calls
  const stopPayloadRef = useRef(null);     // deferred ptt:stop payload

  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);

  const currentChannel = useStore(s => s.activeChannel || s.assignedChannels?.[0] || s.currentChannel);
  const user = useStore(s => s.user);
  const setTransmitting = useStore(s => s.setTransmitting);

  // Core stop — idempotent, safe to call multiple times
  const doStop = useCallback(() => {
    if (!isTxRef.current) return;
    isTxRef.current = false; // prevent re-entry immediately

    // Cancel auto-stop timer
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }

    // Remove all global release listeners
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    const socket = getSocket();
    const duration = startTimeRef.current
      ? (Date.now() - startTimeRef.current) / 1000
      : 0;
    const channelId = activeChannelRef.current;
    const stream = mediaStreamRef.current;
    const recorder = mediaRecorderRef.current;

    // Clear time & media refs (activeChannelRef stays set until onstop
    // so ondataavailable can still emit the final chunk)
    startTimeRef.current = null;
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;

    setTransmitting(false, null);

    const finish = () => {
      // Clear channel ref AFTER all chunks have been emitted
      activeChannelRef.current = null;
      // Release mic
      try { stream?.getTracks().forEach(t => t.stop()); } catch (_) {}
      // Emit ptt:stop AFTER all pending arrayBuffer() promises flush.
      // If promises are still pending, defer — ondataavailable will emit after the last one.
      // Roger beep + haptic: confirms transmission sent
      playPTTBeep('end');
      try { navigator.vibrate?.([30, 40, 30]); } catch (_) {}
      if (socket?.connected && channelId) {
        const payload = { channelId, duration: Math.round(duration * 10) / 10 };
        if (pendingBuffersRef.current > 0) {
          stopPayloadRef.current = { socket, payload };
        } else {
          socket.emit('ptt:stop', payload);
        }
      }
    };

    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = finish; // fires after last ondataavailable
      try { recorder.stop(); } catch (_) { finish(); }
    } else {
      finish();
    }
  }, [setTransmitting]);

  const startTransmitting = useCallback(async () => {
    // Cancel any pending release — user re-pressed within the 300ms window
    if (releaseTimerRef.current) {
      clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = null;
    }
    if (isTxRef.current) return;
    if (!currentChannel || !user) return;

    const socket = getSocket();
    if (!socket?.connected) {
      setError('No conectado al servidor');
      return;
    }

    try {
      // Resume AudioContext if suspended (browser requires user gesture)
      // The AudioContext lives in useSocket; calling getUserMedia itself counts as a gesture

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
        },
      });

      // Mark as transmitting BEFORE setting up listeners
      isTxRef.current = true;
      activeChannelRef.current = currentChannel.id;
      mediaStreamRef.current = stream;
      startTimeRef.current = Date.now();

      // Detect mimeType BEFORE using it
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')
        ? 'audio/mp4;codecs=mp4a.40.2'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/ogg';

      // Notify server & update local state
      socket.emit('ptt:start', { channelId: currentChannel.id, mimeType });
      // Beep + haptic: field environments are loud — vibration confirms mic is live
      playPTTBeep('start');
      try { navigator.vibrate?.(80); } catch (_) {}
      setTransmitting(true, {
        userId: user.id,
        username: user.username,
        avatarColor: user.avatarColor,
      });
      setPermissionGranted(true);
      setError(null);

      // --- MediaRecorder ---

      // Reset deferred-stop state for this transmission
      pendingBuffersRef.current = 0;
      stopPayloadRef.current = null;

      const recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 64000, // 64kbps — better voice quality
      });

      const flushDeferredStop = () => {
        if (pendingBuffersRef.current === 0 && stopPayloadRef.current) {
          const { socket: s, payload } = stopPayloadRef.current;
          stopPayloadRef.current = null;
          if (s?.connected) s.emit('ptt:stop', payload);
        }
      };

      recorder.ondataavailable = (e) => {
        // NOTE: check only activeChannelRef (not isTxRef) so the final chunk
        // fired by recorder.stop() is never dropped during doStop cleanup.
        if (e.data.size > 0 && activeChannelRef.current) {
          const channelSnap = activeChannelRef.current;
          pendingBuffersRef.current++;
          e.data.arrayBuffer().then(buf => {
            const sock = getSocket();
            if (sock?.connected) {
              sock.emit('ptt:audioChunk', {
                channelId: channelSnap,
                chunk: Array.from(new Uint8Array(buf)),
              });
            }
            pendingBuffersRef.current--;
            flushDeferredStop();
          }).catch(() => {
            pendingBuffersRef.current--;
            flushDeferredStop();
          });
        }
      };

      recorder.onerror = (ev) => {
        console.error('[PTT] Recorder error:', ev);
        doStop();
      };

      recorder.start(100); // 100ms chunks
      mediaRecorderRef.current = recorder;

      // --- Auto-stop after 60 seconds ---
      autoStopTimerRef.current = setTimeout(() => {
        console.log('[PTT] Auto-stop: 60s limit reached');
        doStop();
      }, MAX_PTT_MS);

      // --- Document-level release listeners ---
      // These fire even when the PTT button's onMouseLeave fires before onMouseUp
      const onRelease = () => stopTransmittingRef.current?.(); // uses 300ms delay via ref
      const onDisconnect = () => {
        console.log('[PTT] Socket disconnected — stopping transmission');
        doStop();
      };

      document.addEventListener('mouseup', onRelease);
      document.addEventListener('touchend', onRelease);
      document.addEventListener('touchcancel', onRelease);
      socket.once('disconnect', onDisconnect);

      cleanupRef.current = () => {
        document.removeEventListener('mouseup', onRelease);
        document.removeEventListener('touchend', onRelease);
        document.removeEventListener('touchcancel', onRelease);
        socket.off('disconnect', onDisconnect);
      };

    } catch (err) {
      // Reset all state on error
      isTxRef.current = false;
      activeChannelRef.current = null;
      startTimeRef.current = null;
      setTransmitting(false, null);

      if (err.name === 'NotAllowedError') {
        setError('Acceso al micrófono denegado. Permite el acceso en la configuración del navegador.');
        setPermissionGranted(false);
      } else {
        setError('Error al acceder al micrófono: ' + err.message);
      }
      console.error('[PTT] Error starting transmission:', err);
    }
  }, [currentChannel, user, setTransmitting, doStop]); // no circular dep on stopTransmitting

  // Public stop — 300ms delay so the last syllable isn't cut off
  const stopTransmitting = useCallback(() => {
    if (releaseTimerRef.current) return; // already waiting
    releaseTimerRef.current = setTimeout(() => {
      releaseTimerRef.current = null;
      doStop();
    }, 300);
  }, [doStop]);

  // Keep ref up to date so startTransmitting's onRelease can use latest version
  stopTransmittingRef.current = stopTransmitting;

  const requestPermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setPermissionGranted(true);
      setError(null);
      return true;
    } catch {
      setError('Acceso al micrófono denegado. Permite el acceso en tu navegador.');
      return false;
    }
  }, []);

  return { startTransmitting, stopTransmitting, requestPermission, error, permissionGranted };
}
